import { ApiError } from "@/features/api/store";
import { isEmptySale } from "@/pages/sales/utils/localSale";
import { reportSlimSaleWrite } from "@/pages/sales/utils/reportSlimSaleWrite";
import { PharmacyProductWithStockItemMappings, Sale, SaleRecord } from "@meditect/medibase-api-client";
import { LOCAL_SALE_STATUSES, LocalSaleStatus } from "../../enum";
import { Slice } from "../boundStore";

export type LocalSale = {
  errors?: ApiError[]; // use ApiError code to trigger `ApiErrorModal`
  isOpen: boolean;
  sale: Sale;
  openedBy?: string;
  sessionId?: string;
  status: LocalSaleStatus;
};

export type SaleSlice = {
  closeSale: (saleId: string) => void;
  createLocalSale: (sale: Sale) => void;
  endSale: (sale: Sale) => void;
  handleSaleError: (sale: Sale, error: ApiError) => void;
  openSale: (userId: string, sale: Sale) => void;
  removeSale: (saleId: string) => void;
  resetSales: () => void;
  sales: LocalSale[];
  updateLocalSale: (sale: Sale) => void;
  updateLocalSalesFromPharmacyProductMappings: (pharmacyProduct: PharmacyProductWithStockItemMappings) => void;
};

function findSaleIndex(state: SaleSlice, saleId: string): number {
  return state.sales.findIndex(({ sale }) => sale.id === saleId);
}

function updateSaleFields(sale: LocalSale, fields: Partial<LocalSale>): LocalSale {
  return { ...sale, ...fields };
}

export const createSaleSlice: Slice<SaleSlice> = (set) => ({
  sales: [],

  closeSale: (saleId) => {
    set(({ sale: state }) => {
      const index = findSaleIndex(state, saleId);
      if (index < 0) {
        return;
      }

      const isSaleEmpty = isEmptySale(state.sales[index].sale);
      if (isSaleEmpty) {
        state.sales = state.sales.filter(({ sale }) => sale.id !== state.sales[index].sale.id);
      } else {
        state.sales[index] = updateSaleFields(state.sales[index], {
          isOpen: false,
          openedBy: undefined,
          sessionId: undefined,
        });
      }
    });
  },

  createLocalSale: (saleInput) => {
    reportSlimSaleWrite("salesSlice.createLocalSale", saleInput);
    set(({ sale: state }) => {
      const index = findSaleIndex(state, saleInput.id);
      if (index < 0) {
        state.sales.push({
          isOpen: false,
          sale: saleInput,
          status: LOCAL_SALE_STATUSES.LOCAL,
        });
      }
    });
  },

  endSale: (saleInput) => {
    reportSlimSaleWrite("salesSlice.endSale", saleInput);
    set(({ sale: state }) => {
      const index = findSaleIndex(state, saleInput.id);
      if (index >= 0) {
        state.sales[index] = updateSaleFields(state.sales[index], {
          sale: saleInput,
          isOpen: false,
          openedBy: undefined,
          sessionId: undefined,
          status: LOCAL_SALE_STATUSES.SUBMITTING,
          errors: [],
        });
      }
    });
  },

  handleSaleError: (saleInput, error) => {
    set(({ sale: state }) => {
      const index = findSaleIndex(state, saleInput.id);
      if (index >= 0) {
        state.sales[index] = updateSaleFields(state.sales[index], {
          status: LOCAL_SALE_STATUSES.ERROR,
          errors: [error],
        });
      }
    });
  },

  openSale: (userId, saleInput) => {
    reportSlimSaleWrite("salesSlice.openSale", saleInput);
    set(({ sale: state, sessionId }) => {
      const index = findSaleIndex(state, saleInput.id);
      if (index >= 0) {
        state.sales[index] = updateSaleFields(state.sales[index], {
          isOpen: true,
          openedBy: userId,
          sessionId,
        });
      } else {
        state.sales.push({
          isOpen: true,
          sale: saleInput,
          status: LOCAL_SALE_STATUSES.ONGOING,
          openedBy: userId,
          sessionId,
        });
      }
    });
  },

  removeSale: (saleId) => {
    set(({ sale: state }) => {
      const index = findSaleIndex(state, saleId);
      if (index >= 0) {
        state.sales = state.sales.filter(({ sale }) => sale.id !== state.sales[index].sale.id);
      }
    });
  },

  resetSales: () => {
    set(({ sale: state }) => {
      state.sales = [];
    });
  },

  updateLocalSale: (saleInput) => {
    reportSlimSaleWrite("salesSlice.updateLocalSale", saleInput);
    set(({ sale: state }) => {
      const index = findSaleIndex(state, saleInput.id);
      if (index >= 0) {
        const updatedSale = {
          ...saleInput,
          paymentTypes: [...new Set(saleInput.payments.map((p) => p.type))],
          total:
            saleInput.records.reduce((sum, record) => sum + record.price * record.quantity, 0) +
            saleInput.vouchers.reduce((sum, voucher) => sum + voucher.price * voucher.quantity, 0),
          units:
            saleInput.records.reduce((sum, record) => sum + record.quantity, 0) +
            saleInput.vouchers.reduce((sum, voucher) => sum + voucher.quantity, 0),
        };
        state.sales[index] = updateSaleFields(state.sales[index], {
          sale: updatedSale,
        });
      }
    });
  },

  updateLocalSalesFromPharmacyProductMappings: (pharmacyProduct) => {
    const changedStockItemIds = Object.keys(pharmacyProduct.mappings ?? {});

    set(({ sale: state }) => {
      const salesWithUpdatedProduct = state.sales
        .map((s) => s.sale)
        .filter((sale) => sale.records.some((record) => changedStockItemIds.includes(record.stockItem.id)));
      for (const sale of salesWithUpdatedProduct) {
        const index = state.sales.findIndex(({ sale: savedSale }) => savedSale.id === sale.id);
        state.sales[index].sale.records = sale.records.map<SaleRecord>(({ stockItem, ...record }) => {
          // sale might have other pharmacy products that did not change
          if (stockItem.pharmacyProductId !== pharmacyProduct.id) {
            return { ...record, stockItem };
          }

          const id: string = changedStockItemIds.includes(stockItem.id)
            ? (pharmacyProduct?.mappings?.[stockItem.id] as string)
            : stockItem.id;
          return {
            ...record,
            price: pharmacyProduct.stockItems.find((si) => si.id === id)?.salePrice ?? 0, // not sur about this ??
            stockItem: {
              ...stockItem,
              id,
            },
          };
        });
      }
    });
  },
});
